import React from 'react';
import { NavLink, useLocation } from 'react-router';
import { LayoutDashboard, Calendar, FileText, Bell, MoreHorizontal, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { usePermissions } from '@/hooks/use-permissions';
import { useNotificationFeed } from '@/features/notifications/use-notifications';
import { SIDEBAR_GROUPS } from './sidebar';

interface PrimaryTab {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Resource slug — tab is hidden if user can't view this resource.
   *  Omit for tabs available to every authenticated user (e.g. Notifications). */
  slug?: string;
}

// Notifications takes Assets' slot on mobile (Facebook-style): the header bell
// popup is cramped on phones, so the bottom tab opens the full /notifications
// page instead. Assets stays reachable via the "More" sheet (SIDEBAR_GROUPS).
const PRIMARY_TABS: PrimaryTab[] = [
  { to: '/dashboard',           label: 'Dashboard',     icon: LayoutDashboard, slug: 'dashboard' },
  { to: '/my-dispatch',         label: 'My Dispatch',   icon: Calendar,        slug: 'my_dispatch_page' },
  { to: '/job-cards/dashboard', label: 'Job Cards',     icon: FileText,        slug: 'job_cards_page' },
  { to: '/notifications',       label: 'Notifications', icon: Bell },
];

export function BottomNav() {
  const { can } = usePermissions();
  const location = useLocation();
  const { data: feed } = useNotificationFeed();
  const unseen = feed?.unseenCount ?? 0;
  const [moreOpen, setMoreOpen] = React.useState(false);

  // Two-phase mount so the sheet can slide in from the bottom on open and
  // slide back out on close. `mounted` controls DOM presence; `entering`
  // drives the transform/opacity transition that animates the slide+fade.
  const [mounted, setMounted] = React.useState(false);
  const [entering, setEntering] = React.useState(false);
  React.useEffect(() => {
    if (moreOpen) {
      setMounted(true);
      // Double rAF: the first frame lets React commit `mounted=true` so the
      // sheet renders at `translate-y-full`; the second waits until the
      // browser has *painted* that initial state. Flipping `entering` only
      // then guarantees the browser sees a real transform change and runs
      // the slide-in transition (a single rAF batches both class changes
      // into one paint, which is what made open feel instant).
      let id2 = 0;
      const id1 = requestAnimationFrame(() => {
        id2 = requestAnimationFrame(() => setEntering(true));
      });
      return () => {
        cancelAnimationFrame(id1);
        if (id2) cancelAnimationFrame(id2);
      };
    } else {
      setEntering(false);
      const id = window.setTimeout(() => setMounted(false), 250);
      return () => window.clearTimeout(id);
    }
  }, [moreOpen]);

  // Close the More sheet whenever the route changes so a tap-through
  // doesn't leave the overlay sitting on top of the new page.
  React.useEffect(() => { setMoreOpen(false); }, [location.pathname]);

  const visiblePrimary = PRIMARY_TABS.filter((t) => !t.slug || can(t.slug, 'view'));

  const visibleGroups = SIDEBAR_GROUPS
    .map((g) => ({ ...g, items: g.items.filter((i) => !i.slug || can(i.slug, 'view')) }))
    .filter((g) => g.items.length > 0);

  return (
    <>
      {/* "More" bottom sheet — slides up from below, occupies up to 85dvh.
          Lists every group + sub-item the user can reach, categorised the
          same way as the desktop sidebar. */}
      {mounted && (
        <>
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => setMoreOpen(false)}
            className={cn(
              'lg:hidden fixed inset-0 z-40 bg-black/50 transition-opacity duration-300',
              entering ? 'opacity-100' : 'opacity-0',
            )}
          />
          <div
            role="dialog"
            aria-label="More menu"
            className={cn(
              'lg:hidden fixed inset-x-0 bottom-0 z-50 bg-background flex flex-col',
              'rounded-t-xl border-t shadow-xl max-h-[85dvh] pb-[env(safe-area-inset-bottom)]',
              'transition-transform duration-300 ease-out',
              entering ? 'translate-y-0' : 'translate-y-full',
            )}
          >
            {/* Drag handle — purely visual cue that this is a swipe-up sheet. */}
            <div className="pt-2 pb-1 flex justify-center shrink-0">
              <div className="h-1 w-10 rounded-full bg-muted-foreground/30" />
            </div>
            <div className="h-12 border-b flex items-center justify-between px-4 shrink-0">
              <span className="font-semibold">More</span>
              <button
                type="button"
                aria-label="Close"
                onClick={() => setMoreOpen(false)}
                className="h-9 w-9 inline-flex items-center justify-center rounded-md hover:bg-accent"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto p-3 space-y-4">
              {visibleGroups.map((g) => {
                const GroupIcon = g.icon;
                return (
                  <div key={g.label}>
                    <div className="flex items-center gap-2 px-2 pb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      <GroupIcon className="h-3.5 w-3.5" />
                      {g.label}
                    </div>
                    <div className="space-y-0.5">
                      {g.items.map((i) => (
                        <NavLink
                          key={i.to}
                          to={i.to}
                          className={({ isActive }) =>
                            cn(
                              'block px-3 py-2 rounded-md text-sm transition-colors',
                              isActive
                                ? 'bg-accent text-accent-foreground font-medium'
                                : 'hover:bg-accent/50'
                            )
                          }
                        >
                          {i.label}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                );
              })}
            </nav>
          </div>
        </>
      )}

      {/* Bottom bar — 4 primary tabs + More. No horizontal scroll needed at
          this count, so the bar lays out as evenly-spaced flex children. */}
      <nav
        aria-label="Primary"
        className="lg:hidden fixed bottom-0 left-0 right-0 z-30 h-[calc(4rem_+_env(safe-area-inset-bottom))] border-t bg-background"
      >
        {/* Tab row stays a fixed 64px; the extra height from the safe-area
            inset is bar background that fills under the home indicator. */}
        <div className="h-16 flex items-stretch">
          {visiblePrimary.map((t) => {
            const Icon = t.icon;
            return (
              <NavLink
                key={t.to}
                to={t.to}
                className={({ isActive }) =>
                  cn(
                    'flex-1 flex flex-col items-center justify-center gap-0.5 transition-colors',
                    isActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
                  )
                }
              >
                <span className="relative">
                  <Icon className="h-5 w-5" />
                  {t.to === '/notifications' && unseen > 0 && (
                    <span className="absolute -top-1.5 -right-2 min-w-[1rem] h-4 px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] leading-4 text-center">
                      {unseen > 9 ? '9+' : unseen}
                    </span>
                  )}
                </span>
                <span className="text-[10px] leading-tight truncate max-w-[64px]">{t.label}</span>
              </NavLink>
            );
          })}
          <button
            type="button"
            onClick={() => setMoreOpen(true)}
            aria-label="Open more menu"
            aria-expanded={moreOpen}
            className={cn(
              'flex-1 flex flex-col items-center justify-center gap-0.5 transition-colors',
              moreOpen ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <MoreHorizontal className="h-5 w-5" />
            <span className="text-[10px] leading-tight">More</span>
          </button>
        </div>
      </nav>
    </>
  );
}
